import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../../api/axios';
import MicrosoftBookingsControls from './MicrosoftBookingsControls';
import GoogleAppointmentControls from './GoogleAppointmentControls';
import HubSpotMeetingControls from './HubSpotMeetingControls';
import MeetingProviderHealth from './MeetingProviderHealth';
import './MeetingProviderStatus.css';

const PROVIDER_LABELS = {
  none: 'Geen afspraaktool',
  calendly: 'Calendly',
  calcom: 'Cal.com',
  hubspot: 'HubSpot Meetings',
  microsoft_bookings: 'Microsoft Bookings',
  google_appointment_schedule: 'Google Appointment Schedule',
  custom: 'Eigen boekingslink',
};
const PROVIDER_HELP = {
  calendly: 'Volledig beheerd via OAuth en realtime bookingevents. Handmatige Calendly-configuratie blijft ook mogelijk.',
  calcom: 'Native Cal.com-webhook met HMAC-controle voor boeken, verplaatsen, annuleren, afronden en no-show.',
  hubspot: 'HubSpot-account en meetinglink worden beheerd via OAuth. Bookingevents kunnen via de campagnewebhook binnenkomen.',
  microsoft_bookings: 'Hergebruikt het gekoppelde Microsoft-account en synchroniseert Bookings-afspraken periodiek via Microsoft Graph.',
  google_appointment_schedule: 'Hergebruikt het gekoppelde Google-account en detecteert Appointment Schedule-bookings in Google Agenda.',
  custom: 'Gebruik iedere openbare boekingslink en stuur genormaliseerde bookingevents via Make, Zapier, n8n of een eigen integratie.',
};

function MeetingProviderCard({ config, webhookPath, apiBaseUrl, busy, onChange, onSave }) {
  const { id } = useParams();
  const [summary, setSummary] = useState(null);
  const [calendlyStatus, setCalendlyStatus] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [selectedEventType, setSelectedEventType] = useState(config.eventTypeUri || '');
  const [calendlyBusy, setCalendlyBusy] = useState(false);
  const [calendlyError, setCalendlyError] = useState('');
  const enabled = config.provider && config.provider !== 'none';
  const base = String(apiBaseUrl || '').replace(/\/$/, '');
  const webhookUrl = webhookPath ? `${base}${webhookPath}` : '';
  const calendlyWebhookUrl = webhookPath ? `${base}${webhookPath.replace('/webhook/', '/calendly/webhook/')}` : '';
  const calcomWebhookUrl = webhookPath ? `${base}${webhookPath.replace('/webhook/', '/calcom/webhook/')}` : '';
  const change = (field, value) => onChange({ ...config, [field]: value });

  const loadSummary = useCallback(async () => {
    try { setSummary((await api.get(`/agent/campaigns/${id}/meeting-status-summary`)).data); }
    catch { setSummary(null); }
  }, [id]);

  const loadCalendly = useCallback(async () => {
    try {
      const statusResponse = await api.get('/integrations/calendly/status');
      setCalendlyStatus(statusResponse.data);
      if (statusResponse.data.connected) setEventTypes((await api.get('/integrations/calendly/event-types')).data.eventTypes || []);
      else setEventTypes([]);
      setCalendlyError('');
    } catch (error) {
      setCalendlyStatus({ connected: false });
      setEventTypes([]);
      setCalendlyError(error.response?.data?.message || error.response?.data?.error || error.message);
    }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { if (config.provider === 'calendly') loadCalendly(); }, [config.provider, loadCalendly]);
  useEffect(() => { setSelectedEventType(config.eventTypeUri || ''); }, [config.eventTypeUri]);

  const connectCalendly = async () => {
    setCalendlyBusy(true); setCalendlyError('');
    try {
      const response = await api.post('/integrations/calendly/start', { returnPath: `/campaigns/${id}/agent` });
      window.location.assign(response.data.url);
    } catch (error) {
      setCalendlyError(error.response?.data?.message || error.response?.data?.error || error.message);
      setCalendlyBusy(false);
    }
  };

  const useCalendlyEventType = async () => {
    if (!selectedEventType) return;
    setCalendlyBusy(true); setCalendlyError('');
    try {
      const response = await api.patch(`/integrations/calendly/campaigns/${id}/event-type`, { eventTypeUri: selectedEventType });
      onChange({ ...config, ...response.data.config });
      await loadCalendly();
    } catch (error) { setCalendlyError(error.response?.data?.message || error.response?.data?.error || error.message); }
    finally { setCalendlyBusy(false); }
  };

  const counts = summary?.latestCounts || {};
  const providerHelp = PROVIDER_HELP[config.provider];
  const providerManagedLink = config.oauthManaged === true && ['calendly', 'hubspot'].includes(config.provider);
  const providerHasGenericWebhook = ['calendly', 'calcom', 'hubspot', 'microsoft_bookings', 'google_appointment_schedule', 'custom'].includes(config.provider);
  const oauthWebhookManaged = config.provider === 'calendly' && config.oauthManaged === true;
  const manualWebhookEnabled = enabled && config.webhookEnabled === true && !oauthWebhookManaged;
  const displayedWebhookUrl = config.provider === 'calendly' ? calendlyWebhookUrl : config.provider === 'calcom' ? calcomWebhookUrl : webhookUrl;
  const showSimpleType = ['calendly', 'calcom', 'custom'].includes(config.provider) && !providerManagedLink;

  return <section className="campaign-agent-card meeting-provider-card">
    <div className="campaign-agent-card-header">
      <div>
        <h2>Afspraaktool voor deze campagne</h2>
        <p>Iedere campagne kiest zelfstandig een afspraakprovider. De agent gebruikt de boekingslink pas wanneer het subdoel daadwerkelijk een afspraak plannen is.</p>
      </div>
      <div className="campaign-agent-header-actions">
        <Link className="campaign-agent-button secondary link-button" to={`/campaigns/${id}/agent/meeting-reviews`}>Bookingreviews{summary?.openReviews ? ` (${summary.openReviews})` : ''}</Link>
        <button className="campaign-agent-button" onClick={onSave} disabled={busy}>Afspraaktool opslaan</button>
      </div>
    </div>

    <div className="meeting-provider-grid">
      <label>Provider
        <select value={config.provider || 'none'} onChange={(event) => change('provider', event.target.value)}>
          {Object.entries(PROVIDER_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </label>
      {showSimpleType && <label>Type afspraak
        <input value={config.eventType || ''} disabled={!enabled} onChange={(event) => change('eventType', event.target.value)} placeholder="Bijv. kennismakingsgesprek of productdemo" />
      </label>}
      <label className="meeting-provider-wide">Boekingslink
        <input type="url" value={config.bookingUrl || ''} disabled={!enabled || providerManagedLink} onChange={(event) => change('bookingUrl', event.target.value)} placeholder="https://..." />
        {providerManagedLink && <small>Deze link wordt beheerd door de gekoppelde provider.</small>}
      </label>
      <label>Tijdzone
        <input value={config.timezone || 'Europe/Amsterdam'} disabled={!enabled} onChange={(event) => change('timezone', event.target.value)} />
      </label>
      {providerHasGenericWebhook && !oauthWebhookManaged && <label className="planner-checkbox meeting-webhook-toggle">
        <input type="checkbox" checked={config.webhookEnabled === true} disabled={!enabled} onChange={(event) => change('webhookEnabled', event.target.checked)} />
        <span>Externe bookingevents via webhook accepteren</span>
      </label>}
      <label className="planner-checkbox meeting-webhook-toggle">
        <input type="checkbox" checked={config.trackingEnabled !== false} disabled={!enabled} onChange={(event) => change('trackingEnabled', event.target.checked)} />
        <span>Persoonlijke leadtracking toevoegen</span>
      </label>
      <label className="planner-checkbox meeting-webhook-toggle">
        <input type="checkbox" checked={config.prefillLeadDetails !== false} disabled={!enabled} onChange={(event) => change('prefillLeadDetails', event.target.checked)} />
        <span>Naam en e-mailadres vooraf invullen</span>
      </label>
      {config.provider === 'calendly' && config.oauthManaged !== true && <label className="meeting-provider-wide">Calendly signing key
        <input type="password" value={config.calendlySigningKey || ''} onChange={(event) => change('calendlySigningKey', event.target.value)} placeholder={config.calendlySigningKeyConfigured ? 'Signing key is ingesteld — vul alleen in om te vervangen' : 'Signing key uit Calendly'} />
      </label>}
      {config.provider === 'calcom' && <label className="meeting-provider-wide">Cal.com webhook secret
        <input type="password" value={config.calcomWebhookSecret || ''} onChange={(event) => change('calcomWebhookSecret', event.target.value)} placeholder={config.calcomWebhookSecretConfigured ? 'Secret is ingesteld — vul alleen in om te vervangen' : 'Secret uit de Cal.com webhookconfiguratie'} />
        <small>Leadgenflow verifieert hiermee de HMAC-handtekening van Cal.com.</small>
      </label>}
    </div>

    {config.provider === 'calendly' && <div className="meeting-provider-native-panel">
      <div className="meeting-provider-native-head">
        <div><strong>Calendly</strong><p>{calendlyStatus?.connected ? 'Accountbreed gekoppeld. Kies voor deze campagne het juiste Calendly-eventtype.' : 'Koppel Calendly één keer voor automatisch eventtype- en webhookbeheer.'}</p></div>
        {!calendlyStatus?.connected ? <button className="campaign-agent-button secondary" disabled={calendlyBusy} onClick={connectCalendly}>{calendlyBusy ? 'Doorsturen…' : 'Calendly koppelen'}</button> : <span className="calendly-connected-badge">Gekoppeld</span>}
      </div>
      {calendlyError && <div className="planner-warning">{calendlyError}</div>}
      {calendlyStatus?.connected && <div className="calendly-event-type-row">
        <label>Eventtype<select value={selectedEventType} onChange={(event) => setSelectedEventType(event.target.value)}><option value="">Kies een eventtype</option>{eventTypes.map((item) => <option key={item.uri} value={item.uri}>{item.name}{item.duration ? ` · ${item.duration} min` : ''}</option>)}</select></label>
        <button className="campaign-agent-button secondary" disabled={!selectedEventType || calendlyBusy} onClick={useCalendlyEventType}>{calendlyBusy ? 'Opslaan…' : 'Gebruik voor campagne'}</button>
      </div>}
    </div>}

    {config.provider === 'hubspot' && <HubSpotMeetingControls campaignId={id} config={config} onChange={onChange} />}
    {config.provider === 'microsoft_bookings' && <MicrosoftBookingsControls campaignId={id} config={config} onChange={onChange} />}
    {config.provider === 'google_appointment_schedule' && <GoogleAppointmentControls campaignId={id} config={config} onChange={onChange} />}

    <MeetingProviderHealth campaignId={id} provider={config.provider} refreshKey={`${config.updatedAt || ''}:${config.bookingUrl || ''}:${config.externalServiceId || ''}:${config.webhookEnabled || false}`} />

    {providerHelp && <div className="meeting-provider-capability-note"><strong>{PROVIDER_LABELS[config.provider]}</strong><span>{providerHelp}</span></div>}

    {summary && <div className="meeting-status-summary">
      <div><span>Geboekt</span><strong>{counts.booked || 0}</strong></div>
      <div><span>Verplaatst</span><strong>{counts.rescheduled || 0}</strong></div>
      <div><span>Geannuleerd</span><strong>{counts.cancelled || 0}</strong></div>
      <div><span>Afgerond</span><strong>{counts.completed || 0}</strong></div>
      <div><span>No-show</span><strong>{counts.noShow || 0}</strong></div>
      <div><span>Open reviews</span><strong>{summary.openReviews || 0}</strong></div>
    </div>}

    {manualWebhookEnabled && <div className="meeting-webhook-box">
      <strong>{config.provider === 'calendly' ? 'Native Calendly-webhook' : config.provider === 'calcom' ? 'Native Cal.com-webhook' : 'Generieke campagnewebhook'}</strong>
      <p>Gebruik deze URL uitsluitend voor deze campagne.</p>
      {displayedWebhookUrl ? <code>{displayedWebhookUrl}</code> : <span>Sla de instellingen eerst op om de webhook-URL te genereren.</span>}
      {config.provider === 'calendly' && <p className="planner-help">Abonneer op invitee.created en invitee.canceled.</p>}
      {config.provider === 'calcom' && <p className="planner-help">Abonneer minimaal op BOOKING_CREATED, BOOKING_RESCHEDULED en BOOKING_CANCELLED.</p>}
      {!['calendly', 'calcom'].includes(config.provider) && <p className="planner-help">Stuur type meeting_booked, meeting_rescheduled, meeting_cancelled, meeting_completed of meeting_no_show met bij voorkeur inviteeEmail en de Leadgenflow trackingtoken.</p>}
    </div>}
    {enabled && <p className="planner-help">Persoonlijke tracking wordt vóór e-mailmatching gebruikt. Een doorgestuurde link met afwijkende identiteit gaat veilig naar de booking-reviewqueue.</p>}
  </section>;
}

export default MeetingProviderCard;